// src/components/ChatInput.js
import React, { useState, useRef, useEffect } from 'react';
import { Box, InputBase, IconButton, Tooltip } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import MicIcon from '@mui/icons-material/Mic';
import SendIcon from '@mui/icons-material/Send';
import UploadIcon from '@mui/icons-material/UploadFile';
import { speakText } from '../utils/playAudioResponse';
import SupplierInvoiceUpload from './SupplierInvoiceUpload';
import SalesOrderUpload from './SalesOrderUpload';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { supabase } from '../supabaseClient';
import { getTableHint } from '../constants/tableHints';

const UPLOADABLE_TABLES = ['VendorInvoice', 'PurchaseOrder', 'GRN', 'SalesOrder'];

const ChatInput = ({
  onSendMessage,
  disabled,
  onVoiceModeChange,
  shouldTurnOffVoice,
  domain,
  selectedTable,
  quickButtons,
  onUploadToggle          // ⭐ NEW prop
}) => {

  // ----------------------------
  // INPUT + UI STATE
  // ----------------------------
  const [input, setInput] = useState('');
  const [isAnalytics, setIsAnalytics] = useState(false);
  const [useVoice, setUseVoice] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [dotCount, setDotCount] = useState(0);
  const [showUpload, setShowUpload] = useState(false);

  const recognitionRef = useRef(null);

  // Keep upload state in sync with parent (ChatInterface)
  const handleToggleUpload = () => {
    setShowUpload(prev => {
      const next = !prev;
      if (onUploadToggle) {
        onUploadToggle(next);
      }
      return next;
    });
  };

  // Table supports upload
  const isUploadEnabledByTable = UPLOADABLE_TABLES.includes(selectedTable);

  // ----------------------------
  // PERMISSIONS
  // ----------------------------
  const [canUpload, setCanUpload] = useState(false);
  const [canChat, setCanChat] = useState(true);

  useEffect(() => {
    const loadPermissions = async () => {
      const { data: userData } = await supabase.auth.getUser();
      const email = userData?.user?.email;

      if (!email || !domain) {
        setCanUpload(false);
        setCanChat(false);
        return;
      }

      // Palmtop is always available to every user, no Supabase permission needed.
      if (domain === 'Palmtop') {
        setCanUpload(false);
        setCanChat(true);
        return;
      }

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('agents')
        .eq('email', email)
        .single();

      const agents = profile?.agents || {};


  const domainToAgent = {
  // SALES
  'Sales Order': 'sales',
  'Business Partner': 'sales',
  'Sales Quotation': 'sales',
  'Opportunities': 'sales',
  // PURCHASE
  'Vendor': 'purchase',
  'Purchase Orders': 'purchase',
  'Purchase Requisition': 'purchase',
  'Goods Movement': 'purchase',
  'Vendor Invoice': 'purchase',

  // HR
  'Human Resource': 'hr',
  'Policy Repository': 'hr',

  // FINANCE
  'Cost Center': 'finance',
  'Analytics': 'finance',
  'Finance Analyst': 'finance',
  'Vendor Ledger': 'finance',

  // INVENTORY
  'Inventory': 'inventory',
};


      const agentKey = domainToAgent[domain];

      if (!agentKey) {
        setCanUpload(false);
        setCanChat(false);
        return;
      }

      const hasUpload = agents?.[agentKey]?.includes('upload');
      const hasChat = agents?.[agentKey]?.includes('chat');

      setCanUpload(Boolean(hasUpload));
      setCanChat(Boolean(hasChat));
    };

    loadPermissions();
  }, [domain]);

  // ----------------------------
  // SUBMIT LOGIC
  // ----------------------------
  const handleSubmit = (e, override, isVoiceInput = false) => {
    if (e) e.preventDefault();

    const message = override ?? input;
    if (!canChat) return;

    if (disabled || !String(message).trim()) return;

    onSendMessage({
      text: message,
      isTable: isAnalytics,
      isVoiceInput,
    });

    setInput('');
    setIsAnalytics(false);
  };

  // ----------------------------
  // VOICE INPUT LOGIC
  // ----------------------------
  const startSpeechRecognition = () => {
    if (!canChat) return;
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Speech recognition not supported in this browser.');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = true;

    recognition.onstart = () => setIsListening(true);

    recognition.onresult = (event) => {
      let finalTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        const chunk = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalTranscript += chunk;
        else setInput(chunk);
      }
      if (finalTranscript.trim()) {
        setInput(finalTranscript);
        setIsListening(false);
        handleSubmit(null, finalTranscript, true);
      }
    };

    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);

    recognition.start();
    recognitionRef.current = recognition;
  };

  const stopSpeechRecognition = () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.abort();
      } finally {
        recognitionRef.current = null;
      }
    }
    setIsListening(false);
  };

  const cancelPlayback = () => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
  };

  // If an external signal tells us to turn off voice mode, do so here:
  useEffect(() => {
    if (shouldTurnOffVoice) {
      setUseVoice(false);
    }
  }, [shouldTurnOffVoice]);

  // Voice mode toggling & side effects
  useEffect(() => {
    if (!canChat) {
      stopSpeechRecognition();
      cancelPlayback();
      setUseVoice(false);
      return;
    }

    if (useVoice) {
      startSpeechRecognition();
    } else {
      stopSpeechRecognition();
      cancelPlayback();
    }
    onVoiceModeChange?.(useVoice);
  }, [useVoice, canChat]);

  // Dots animation
  useEffect(() => {
    let interval = null;
    const active =
      useVoice && (isListening || isSpeaking || (!isListening && !isSpeaking));

    if (active) {
      interval = setInterval(
        () => setDotCount((prevDot) => (prevDot + 1) % 4),
        500
      );
    } else {
      setDotCount(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [useVoice, isListening, isSpeaking]);

  // ----------------------------
  // PLACEHOLDER TEXT
  // ----------------------------
  // Table-specific context so the user knows what the selected agent covers.
  const tableHint = getTableHint(domain, selectedTable);

  const basePlaceholder = !canChat
    ? 'Chat has been disabled by admin'
    : isListening
    ? 'Listening'
    : isSpeaking
    ? 'Playing Response'
    : useVoice
    ? 'Voice mode is active...'
    : tableHint
    ? `Ask anything about ${tableHint.placeholder}...`
    : 'Ask anything...';

  const placeholderText = useVoice
    ? basePlaceholder + '.'.repeat(dotCount)
    : basePlaceholder;

  // ----------------------------
  // RENDER
  // ----------------------------

  return (
    <Box sx={{ width: '100%' }}>
      {/* UPLOAD PANEL */}
      {isUploadEnabledByTable && canUpload && showUpload && (
        <Box
          sx={{
            mb: 1.5,
            p: 2,
            border: '1px dashed #555',
            borderRadius: 2,
          }}
        >
          {selectedTable === 'SalesOrder' ? (
            <SalesOrderUpload />
          ) : (
            <SupplierInvoiceUpload documentType={selectedTable} />
          )}
        </Box>
      )}

      <Box
        sx={(theme) => ({
          width: '100%',
          maxWidth: 860,
          mx: 'auto',
          backgroundColor:
            theme.palette.mode === 'light' ? '#eaeaea' : '#2b2b2b',
          color: theme.palette.text.primary,
          borderRadius: '16px',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 180,
          boxShadow: (theme.palette.mode === 'light'
            ? '0 0 0 1px rgba(0,0,0,0.10) inset'
            : '0 0 0 1px rgba(255,255,255,0.06) inset'),
        })}
      >
        <form onSubmit={handleSubmit}>
          <InputBase
            placeholder={placeholderText}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
            disabled={disabled || !canChat}
            multiline
            minRows={1}
            maxRows={5}
            fullWidth
            sx={{
              '& .MuiInputBase-input': {
                padding: '12px 16px',
                color: (theme) =>
                  theme.palette.mode === 'light'
                    ? '#333'
                    : theme.palette.text.primary,
                fontSize: '14px',
              },
              '& .MuiInputBase-input::placeholder': {
                color: (theme) =>
                  theme.palette.mode === 'light'
                    ? 'rgba(0,0,0,0.6)'
                    : theme.palette.text.primary,
              },
            }}
          />
        </form>

        {/* TABLE CONTEXT — what this agent/table can answer */}
        {canChat && tableHint && !useVoice && (
          <Box
            sx={{
              px: '16px',
              mt: 0.25,
              fontSize: '11.5px',
              lineHeight: 1.4,
              color: (theme) =>
                theme.palette.mode === 'light'
                  ? 'rgba(0,0,0,0.55)'
                  : 'rgba(255,255,255,0.55)',
            }}
          >
            {tableHint.description}
          </Box>
        )}

        {/* ICON ROW */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mt: 1,
            color: (theme) =>
              theme.palette.mode === 'light' ? 'rgba(0,0,0,0.65)' : '#d0d0d0',
          }}
        >
          {/* ANALYTICS */}
          {canChat && (
            <>
              <Tooltip title={isAnalytics ? 'Analytics on - the reply will include a chart' : 'Analytics - reply with a chart'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setIsAnalytics((p) => !p)}
                    sx={{
                      color: (theme) =>
                        isAnalytics
                          ? '#fff'
                          : theme.palette.mode === 'light'
                          ? 'rgba(0,0,0,0.65)'
                          : '#d0d0d0',
                      bgcolor: isAnalytics ? '#1976d2' : 'transparent',
                      '&:hover': {
                        bgcolor: (theme) =>
                          isAnalytics
                            ? '#1565c0'
                            : theme.palette.mode === 'light'
                            ? 'rgba(0,0,0,0.08)'
                            : 'rgba(255,255,255,0.08)',
                      },
                    }}
                  >
                    <BarChartIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              {/* VOICE */}
              <Tooltip title={useVoice ? 'Stop voice' : 'Voice'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setUseVoice((p) => !p)}
                  >
                    <MicIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}

          {/* UPLOAD ICON — now informs parent */}
          {isUploadEnabledByTable && canUpload && (
            <Tooltip title={showUpload ? 'Hide upload' : 'Upload'}>
              <IconButton
                size="small"
                onClick={handleToggleUpload}
              >
                <UploadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {/* SEND BUTTON */}
          <Tooltip title="Send">
            <span>
              <IconButton
                size="small"
                onClick={handleSubmit}
                disabled={disabled || !input.trim() || !canChat}
                sx={(theme) => {
                  const isLight = theme.palette.mode === 'light';
                  const active = input.trim() && canChat;
                  return {
                    color: isLight ? (active ? '#fff' : 'rgba(0,0,0,0.4)') : '#111',
                    backgroundColor: active
                      ? isLight
                        ? '#1976d2'
                        : '#fff'
                      : isLight
                      ? 'rgba(0,0,0,0.08)'
                      : 'rgba(255,255,255,0.2)',
                    '&.Mui-disabled': {
                      color: isLight ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.5)',
                    },
                  };
                }}
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* QUICK BUTTONS */}
        {quickButtons && canChat && (
          <Box
            sx={{
              mt: 1,
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            {quickButtons}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ChatInput;
